import type { MergedTheme, BrandTheme } from '@owner/theme'
import { storeToRefs } from 'pinia'
import { computed, type Ref } from 'vue'

import { type CoreLocation, type CoreBrand } from '@/core/store/types'
import { type Brand } from '@/core/types'
import { OlympusClient } from '@/OlympusClient'
import globalStore, { useMappedGetter } from '@/store'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { type CoreStoreBrand, useCoreStore } from '../useCoreStore'

/**
 * Returns the current active brand from the store
 */
export const useActiveBrand = () => {
  // TODO: uncomment this after the new brand view has been released + feature flag turned on
  // ticket: https://linear.app/owner/issue/RES-3794/re-add-inject-pattern
  /**
   * If the ACTIVE_BRAND_CONTEXT_KEY is set, that means we are inside the
   * ActiveBrandProvider and that we definitely have data in the store. This allows us
   * to ignore the potential of an undefined value for activeBrandId and activeBrand
   */
  // if (!injectLocal(ACTIVE_BRAND_CONTEXT_KEY)) {
  //   throw new Error(
  //     'should not use useActiveBrand outside of the active brand context'
  //   )
  // }
  const { enabled: usePiniaStore } = useReactiveFeatureFlag(
    'hermes.usePiniaStore'
  )

  const {
    activeBrand: piniaActiveBrand,
    activeBrandMergedTheme: piniaActiveBrandMergedTheme,
  } = storeToRefs(useCoreStore())
  const { updateActiveBrandData } = useCoreStore()

  const vuexActiveBrand = useMappedGetter<CoreBrand>('core/brand/active/*')
  const activeBrand = computed(() =>
    usePiniaStore.value
      ? (piniaActiveBrand as Ref<CoreStoreBrand>).value // casting activeBrand as defined due to the active brand context
      : vuexActiveBrand.value
  )

  const activeBrandId = computed(() => activeBrand.value.id)
  const domain = computed(() => activeBrand.value.domain)
  const name = computed(() => activeBrand.value.name)
  const links = computed(() => activeBrand.value.links)
  const logo = computed(() => activeBrand.value.assets.logo)
  const highestSubscriptionTier = computed(
    () => activeBrand.value.metadata.highestSubscriptionTier
  )

  const vuexBrandLocations = useMappedGetter<CoreLocation[]>(
    'core/brand/active/locations'
  )
  const locations = computed(
    () =>
      (usePiniaStore.value
        ? piniaActiveBrand.value?.locations
        : vuexBrandLocations.value) ?? []
  )
  const isMultiLocation = computed(() => locations.value.length > 1)
  const isSingleLocation = computed(() => locations.value.length === 1)
  const locationsUnderCollections = computed(() =>
    locations.value
      .filter(
        (location) => location.paymentPastDue && location.status !== 'cancelled'
      )
      .map((location) => ({ id: location.id, name: location.name }))
  )
  const locationsWithoutStripeConnected = computed(() =>
    locations.value.filter((location) => !location.processing.stripe?.id)
  )
  const locationsWithoutYextConnected = computed(() =>
    locations.value.filter((location) => !location.listingsUpdatedAt)
  )

  const getLocationById = computed(() => {
    return (locationId: string) =>
      locations.value.find((loc) => loc.id === locationId)
  })

  const getLocationNameById = computed(() => {
    return (locationId: string) => getLocationById.value(locationId)?.name ?? ''
  })

  const getLocationPosById = computed(() => {
    return (locationId: string | undefined) =>
      locationId
        ? (getLocationById.value(locationId)?.pos ?? undefined)
        : undefined
  })

  const vuexActiveBrandTheme = useMappedGetter<BrandTheme | undefined>(
    'core/brand/active/theme'
  )
  const theme = computed(() =>
    usePiniaStore.value
      ? piniaActiveBrand.value?.theme
      : vuexActiveBrandTheme.value
  )

  const vuexActiveBrandMergedTheme = useMappedGetter<MergedTheme | undefined>(
    'core/brand/active/mergedTheme'
  )
  const mergedTheme = computed(() =>
    usePiniaStore.value
      ? piniaActiveBrandMergedTheme.value
      : vuexActiveBrandMergedTheme.value
  )

  const updateBrand = async ({
    brandId,
    update,
  }: {
    brandId: string
    update: Partial<Brand>
  }) => {
    const brand = await OlympusClient.brandsV0.brand.updateBrand({
      body: update,
      params: {
        brandId,
      },
    })

    updateActiveBrandData(brand)
    globalStore.commit('core/SET_BRAND', brand)
  }

  const updateDomain = async ({
    brandId,
    domain,
  }: {
    brandId: string
    domain: string | null
  }) => {
    updateActiveBrandData({ ...activeBrand.value, domain })
    globalStore.commit('core/SET_BRAND_DOMAIN', { brandId, domain })
  }

  return {
    activeBrand,
    activeBrandId,
    domain,
    getLocationById,
    getLocationNameById,
    getLocationPosById,
    highestSubscriptionTier,
    isMultiLocation,
    isSingleLocation,
    links,
    locations,
    locationsUnderCollections,
    locationsWithoutStripeConnected,
    locationsWithoutYextConnected,
    logo,
    mergedTheme,
    name,
    theme,
    updateBrand,
    updateDomain,
  }
}
